import { SshMessageReader } from './message-reader.js';
import { SshMessageWriter } from './message-writer.js';
import { PacketReader, wrapPacket, type PacketCipher } from './packets.js';
import {
	SSH_MSG_DISCONNECT,
	SSH_MSG_IGNORE,
	SSH_MSG_UNIMPLEMENTED,
	SSH_MSG_DEBUG,
	SSH_MSG_SERVICE_REQUEST,
	SSH_MSG_SERVICE_ACCEPT,
	SSH_MSG_EXT_INFO,
	SSH_MSG_KEXINIT,
	SSH_MSG_NEWKEYS,
	SSH_MSG_KEX_ECDH_INIT,
	SSH_MSG_KEX_ECDH_REPLY,
	SSH_MSG_PING,
	SSH_MSG_PONG,
	CLIENT_VERSION,
	KEX_ALGORITHMS,
	HOST_KEY_ALGORITHMS,
	CIPHER_ALGORITHMS,
	MAC_ALGORITHMS,
	COMPRESSION_ALGORITHMS,
	SSH_DISCONNECT_PROTOCOL_ERROR,
	SSH_DISCONNECT_KEY_EXCHANGE_FAILED,
	SSH_DISCONNECT_HOST_KEY_NOT_VERIFIABLE,
} from './constants.js';
import {
	generateKexKeyPair,
	computeSharedSecret,
	computeExchangeHash,
	rawBytesToMpint,
} from '../crypto/kex.js';
import { deriveSessionKeys } from '../crypto/key-derivation.js';
import { Aes128GcmCipher } from '../crypto/cipher.js';
import { verifyHostKey } from '../crypto/host-key.js';
import type { DuplexStream } from '../types.js';
import { Emitter, Disposable, type Logger, noopLogger } from '@ts-ssh2/common';

/**
 * Transport protocol states.
 */
const enum TransportState {
	/** Waiting to exchange version strings. */
	VersionExchange,
	/** Sent/receiving KEXINIT messages. */
	KexInit,
	/** Performing the key exchange algorithm (ECDH). */
	Kex,
	/** Waiting for NEWKEYS. */
	NewKeys,
	/** Transport is encrypted and ready for upper-layer messages. */
	Established,
	/** Transport has been disconnected. */
	Disconnected,
}

/**
 * The negotiated algorithm set after KEXINIT exchange.
 */
type NegotiatedAlgorithms = {
	kex: string;
	hostKey: string;
	cipherC2S: string;
	cipherS2C: string;
	macC2S: string;
	macS2C: string;
	compressionC2S: string;
	compressionS2C: string;
};

/**
 * Host key verification callback.
 * Return true to accept the host key, false to reject.
 */
export type HostKeyVerifier = (
	algorithm: string,
	hostKeyBlob: Uint8Array,
) => Promise<boolean> | boolean;

/**
 * A disconnect event from the remote side.
 */
export type DisconnectEvent = {
	reasonCode: number;
	description: string;
};

/**
 * SSH transport protocol layer.
 *
 * Handles version exchange, key exchange negotiation, ECDH key exchange,
 * and NEWKEYS activation. After handshake completes, dispatches upper-layer
 * messages (auth, connection) via the onMessage emitter.
 *
 * Supports re-keying: if the server sends a new KEXINIT at any time,
 * the transport re-enters the key exchange state machine.
 */
export class SshTransportProtocol extends Disposable {
	private state = TransportState.VersionExchange;

	// i/o
	private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
	private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;

	// packet framing
	private packetReader = new PacketReader();
	private cipher: PacketCipher | null = null;
	private sendSeq = 0;
	private recvSeq = 0;

	// version exchange
	private serverVersion = '';
	private versionBuffer = new Uint8Array(0);

	// kexinit payloads (needed for exchange hash)
	private clientKexInitPayload: Uint8Array | null = null;
	private serverKexInitPayload: Uint8Array | null = null;
	private negotiated: NegotiatedAlgorithms | null = null;

	// session id (H from the first key exchange, never changes)
	private sessionId: Uint8Array | null = null;

	// server extensions from SSH_MSG_EXT_INFO (RFC 8308)
	private extensions = new Map<string, string>();

	// pending pong waiters for OpenSSH transport-level ping
	private pendingPongs: Array<{ resolve: () => void; reject: (err: Error) => void }> = [];

	// serializes send operations so sequence numbers and cipher IVs don't race
	private sendLock: Promise<void> = Promise.resolve();

	// host key verification
	private hostKeyVerifier: HostKeyVerifier;

	// logging
	private logger: Logger;

	// events
	/** Emits upper-layer message payloads (auth, connection protocol). */
	readonly onMessage = new Emitter<Uint8Array>();
	/** Emits when the transport is disconnected. */
	readonly onDisconnect = new Emitter<DisconnectEvent>();
	/** Emits when the handshake (version + kex + newkeys) is complete. */
	readonly onReady = new Emitter<void>();
	/** Emits debug messages from the server. */
	readonly onDebug = new Emitter<string>();

	constructor(hostKeyVerifier?: HostKeyVerifier, logger?: Logger) {
		super();
		this.hostKeyVerifier = hostKeyVerifier ?? (() => true);
		this.logger = logger ?? noopLogger;
	}

	/**
	 * Start the transport protocol over a duplex stream.
	 * Performs version exchange and key exchange, then enters the
	 * established state where upper-layer messages are dispatched.
	 */
	async run(stream: DuplexStream, signal?: AbortSignal): Promise<void> {
		this.reader = stream.readable.getReader();
		this.writer = stream.writable.getWriter();

		try {
			// send client version string
			this.logger.debug('sending client version');
			await this.sendVersion();

			// main read loop
			await this.readLoop(signal);
		} catch (error) {
			if (this.state !== TransportState.Disconnected) {
				this.state = TransportState.Disconnected;
				const message = error instanceof Error ? error.message : String(error);
				this.onDisconnect.emit({
					reasonCode: SSH_DISCONNECT_PROTOCOL_ERROR,
					description: message,
				});
			}
			throw error;
		} finally {
			this.reader?.releaseLock();
			this.writer?.releaseLock();
			this.reader = null;
			this.writer = null;
		}
	}

	/**
	 * Send an upper-layer message payload.
	 * Frames and optionally encrypts the payload before sending.
	 * Serialized to prevent concurrent sends from racing on sequence numbers and cipher IVs.
	 */
	async send(payload: Uint8Array): Promise<void> {
		if (this.state === TransportState.Disconnected) {
			throw new Error('Transport is disconnected');
		}
		const op = this.sendLock.then(async () => {
			const wire = await wrapPacket(payload, this.sendSeq, this.cipher);
			this.sendSeq = (this.sendSeq + 1) >>> 0;
			await this.writer!.write(wire);
		});
		this.sendLock = op.catch(() => {}); // keep chain alive on error
		return op;
	}

	/**
	 * Send SSH_MSG_DISCONNECT and close the transport.
	 */
	async disconnect(reasonCode: number, description: string): Promise<void> {
		if (this.state === TransportState.Disconnected) return;

		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_DISCONNECT);
		w.writeUint32(reasonCode);
		w.writeString(description);
		w.writeString(''); // language tag

		try {
			await this.send(w.toUint8Array());
		} catch {
			// best-effort
		}

		this.state = TransportState.Disconnected;
		this.onDisconnect.emit({ reasonCode, description });
	}

	/**
	 * Request a service (e.g. "ssh-userauth").
	 * Sends SSH_MSG_SERVICE_REQUEST and waits for SSH_MSG_SERVICE_ACCEPT.
	 */
	async requestService(serviceName: string): Promise<void> {
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_SERVICE_REQUEST);
		w.writeString(serviceName);
		await this.send(w.toUint8Array());
	}

	/** The session identifier (exchange hash from first key exchange). */
	getSessionId(): Uint8Array {
		if (!this.sessionId) {
			throw new Error('Session ID not available: key exchange not yet completed');
		}
		return this.sessionId;
	}

	/** Whether the server advertised `ping@openssh.com` via EXT_INFO. */
	get supportsOpenSshPing(): boolean {
		return this.extensions.has('ping@openssh.com');
	}

	/**
	 * Send an OpenSSH transport-level ping.
	 * Resolves when the matching pong is received.
	 * Only valid when `supportsOpenSshPing` is true.
	 */
	async sendPing(): Promise<void> {
		const data = crypto.getRandomValues(new Uint8Array(8));
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_PING);
		w.writeString(data);

		const waiter: { resolve: () => void; reject: (err: Error) => void } = {
			resolve: undefined!,
			reject: undefined!,
		};
		const pongPromise = new Promise<void>((resolve, reject) => {
			waiter.resolve = resolve;
			waiter.reject = reject;
		});
		this.pendingPongs.push(waiter);

		try {
			await this.send(w.toUint8Array());
		} catch (err) {
			// remove the specific waiter we registered (not blindly the last one)
			const idx = this.pendingPongs.indexOf(waiter);
			if (idx !== -1) this.pendingPongs.splice(idx, 1);
			throw err;
		}

		return pongPromise;
	}

	// ── version exchange ───────────────────────────────────────────────

	private async sendVersion(): Promise<void> {
		const versionLine = CLIENT_VERSION + '\r\n';
		const encoded = new TextEncoder().encode(versionLine);
		await this.writer!.write(encoded);
	}

	/**
	 * Process bytes during version exchange.
	 * SSH version strings are line-based (terminated by \r\n).
	 * Lines not starting with "SSH-" are ignored (banner lines).
	 * Returns true if version exchange completed and we should process remaining bytes.
	 */
	private processVersionExchange(chunk: Uint8Array): { done: boolean; remaining: Uint8Array } {
		// append to version buffer
		const combined = new Uint8Array(this.versionBuffer.length + chunk.length);
		combined.set(this.versionBuffer);
		combined.set(chunk, this.versionBuffer.length);
		this.versionBuffer = combined;

		// scan for \n
		while (true) {
			const newlineIdx = this.versionBuffer.indexOf(0x0a); // \n
			if (newlineIdx === -1) {
				return { done: false, remaining: new Uint8Array(0) };
			}

			// extract the line (without \r\n)
			let lineEnd = newlineIdx;
			if (lineEnd > 0 && this.versionBuffer[lineEnd - 1] === 0x0d) {
				lineEnd--; // strip \r
			}
			const line = new TextDecoder().decode(this.versionBuffer.subarray(0, lineEnd));
			const afterLine = this.versionBuffer.subarray(newlineIdx + 1);

			if (line.startsWith('SSH-')) {
				// validate version
				if (!line.startsWith('SSH-2.0-')) {
					throw new Error(`Unsupported SSH version: ${line}`);
				}
				this.serverVersion = line;
				this.logger.debug(`server version: ${line}`);
				this.versionBuffer = new Uint8Array(0);
				return { done: true, remaining: afterLine };
			}

			// banner line, skip it
			this.versionBuffer = afterLine;
		}
	}

	// ── main read loop ─────────────────────────────────────────────────

	private async readLoop(signal?: AbortSignal): Promise<void> {
		while (this.state !== TransportState.Disconnected) {
			signal?.throwIfAborted();

			const result = await this.reader!.read();
			if (result.done) {
				this.state = TransportState.Disconnected;
				this.onDisconnect.emit({
					reasonCode: SSH_DISCONNECT_PROTOCOL_ERROR,
					description: 'Connection closed by remote host',
				});
				return;
			}

			const data = result.value;

			if (this.state === TransportState.VersionExchange) {
				const { done, remaining } = this.processVersionExchange(data);
				if (!done) continue;

				// version exchange complete, start kex
				this.state = TransportState.KexInit;
				await this.sendKexInit();

				// feed remaining bytes to packet reader
				if (remaining.length > 0) {
					this.packetReader.append(remaining);
				}
			} else {
				this.packetReader.append(data);
			}

			// process available packets
			await this.processPackets();
		}
	}

	private async processPackets(): Promise<void> {
		while (this.state !== TransportState.Disconnected) {
			const payload = await this.packetReader.nextPacket(this.recvSeq);
			if (!payload) break;

			this.recvSeq = (this.recvSeq + 1) >>> 0;
			await this.handlePacket(payload);
		}
	}

	// ── packet dispatch ────────────────────────────────────────────────

	private async handlePacket(payload: Uint8Array): Promise<void> {
		if (payload.length === 0) {
			throw new Error('Received empty packet');
		}

		const msgType = payload[0]!;

		switch (msgType) {
			case SSH_MSG_DISCONNECT:
				this.handleDisconnect(payload);
				return;
			case SSH_MSG_IGNORE:
				return; // silently discard
			case SSH_MSG_UNIMPLEMENTED:
				return; // log and continue
			case SSH_MSG_DEBUG:
				this.handleDebug(payload);
				return;
			case SSH_MSG_EXT_INFO:
				this.handleExtInfo(payload);
				return;
			case SSH_MSG_SERVICE_ACCEPT:
				// service accepted — upper layer will handle this through onMessage
				this.onMessage.emit(payload);
				return;
			case SSH_MSG_KEXINIT:
				await this.handleKexInit(payload);
				return;
			case SSH_MSG_KEX_ECDH_REPLY:
				await this.handleKexReply(payload);
				return;
			case SSH_MSG_NEWKEYS:
				await this.handleNewKeys();
				return;
			case SSH_MSG_PING:
				await this.handlePing(payload);
				return;
			case SSH_MSG_PONG:
				this.handlePong(payload);
				return;
			default:
				// upper-layer messages (auth, connection)
				if (this.state === TransportState.Established) {
					this.onMessage.emit(payload);
				} else {
					// unexpected message during kex — send UNIMPLEMENTED
					await this.sendUnimplemented(this.recvSeq - 1);
				}
				return;
		}
	}

	private handleDisconnect(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const reasonCode = reader.readUint32();
		const description = reader.readStringAsText();
		this.logger.debug(`received DISCONNECT reason=${reasonCode} description="${description}"`);
		this.state = TransportState.Disconnected;
		this.onDisconnect.emit({ reasonCode, description });
	}

	private handleDebug(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		reader.readBoolean(); // always_display
		const message = reader.readStringAsText();
		this.onDebug.emit(message);
	}

	private handleExtInfo(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const numExtensions = reader.readUint32();

		for (let i = 0; i < numExtensions; i++) {
			const name = reader.readStringAsText();
			const value = reader.readStringAsText();
			this.extensions.set(name, value);
		}

		this.logger.debug(
			`received EXT_INFO with ${numExtensions} extension(s): ${[...this.extensions.keys()].join(', ')}`,
		);
	}

	private async handlePing(payload: Uint8Array): Promise<void> {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const data = reader.readString();

		this.logger.debug('received PING, sending PONG');

		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_PONG);
		w.writeString(data);
		await this.send(w.toUint8Array());
	}

	private handlePong(_payload: Uint8Array): void {
		const waiter = this.pendingPongs.shift();
		if (waiter) {
			waiter.resolve();
		}
	}

	private async sendUnimplemented(seqNum: number): Promise<void> {
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_UNIMPLEMENTED);
		w.writeUint32(seqNum);
		await this.send(w.toUint8Array());
	}

	// ── key exchange ───────────────────────────────────────────────────

	private async sendKexInit(): Promise<void> {
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_KEXINIT);

		// 16 bytes cookie
		const cookie = new Uint8Array(16);
		crypto.getRandomValues(cookie);
		w.writeRawBytes(cookie);

		// algorithm lists
		w.writeNameList(KEX_ALGORITHMS);
		w.writeNameList(HOST_KEY_ALGORITHMS);
		w.writeNameList(CIPHER_ALGORITHMS); // encryption c2s
		w.writeNameList(CIPHER_ALGORITHMS); // encryption s2c
		w.writeNameList(MAC_ALGORITHMS); // mac c2s
		w.writeNameList(MAC_ALGORITHMS); // mac s2c
		w.writeNameList(COMPRESSION_ALGORITHMS); // compression c2s
		w.writeNameList(COMPRESSION_ALGORITHMS); // compression s2c
		w.writeNameList([]); // languages c2s
		w.writeNameList([]); // languages s2c
		w.writeBoolean(false); // first_kex_packet_follows
		w.writeUint32(0); // reserved

		const payload = w.toUint8Array();
		this.clientKexInitPayload = payload;

		await this.send(payload);
	}

	private async handleKexInit(payload: Uint8Array): Promise<void> {
		this.serverKexInitPayload = payload;

		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		reader.skip(16); // cookie

		const serverKex = reader.readNameList();
		const serverHostKey = reader.readNameList();
		const serverCipherC2S = reader.readNameList();
		const serverCipherS2C = reader.readNameList();
		const serverMacC2S = reader.readNameList();
		const serverMacS2C = reader.readNameList();
		const serverCompressionC2S = reader.readNameList();
		const serverCompressionS2C = reader.readNameList();

		// negotiate: client preference order, first match wins
		this.negotiated = {
			kex: negotiate([...KEX_ALGORITHMS], serverKex, 'key exchange'),
			hostKey: negotiate([...HOST_KEY_ALGORITHMS], serverHostKey, 'host key'),
			cipherC2S: negotiate([...CIPHER_ALGORITHMS], serverCipherC2S, 'cipher (client to server)'),
			cipherS2C: negotiate([...CIPHER_ALGORITHMS], serverCipherS2C, 'cipher (server to client)'),
			macC2S: negotiate([...MAC_ALGORITHMS], serverMacC2S, 'MAC (client to server)'),
			macS2C: negotiate([...MAC_ALGORITHMS], serverMacS2C, 'MAC (server to client)'),
			compressionC2S: negotiate(
				[...COMPRESSION_ALGORITHMS],
				serverCompressionC2S,
				'compression (client to server)',
			),
			compressionS2C: negotiate(
				[...COMPRESSION_ALGORITHMS],
				serverCompressionS2C,
				'compression (server to client)',
			),
		};

		this.logger.debug(
			`negotiated kex=${this.negotiated.kex} hostKey=${this.negotiated.hostKey} cipher=${this.negotiated.cipherC2S}`,
		);

		// if we haven't sent our kexinit yet (server-initiated rekey), send now
		if (!this.clientKexInitPayload) {
			await this.sendKexInit();
		}

		this.state = TransportState.Kex;
		await this.sendKexEcdhInit();
	}

	// ── ECDH key exchange ──────────────────────────────────────────────

	private kexKeyPair: { publicKey: Uint8Array; privateKey: CryptoKey } | null = null;

	private async sendKexEcdhInit(): Promise<void> {
		this.kexKeyPair = await generateKexKeyPair();

		this.logger.debug('sending KEX_ECDH_INIT');
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_KEX_ECDH_INIT);
		w.writeString(this.kexKeyPair.publicKey);
		await this.send(w.toUint8Array());
	}

	private async handleKexReply(payload: Uint8Array): Promise<void> {
		if (this.state !== TransportState.Kex || !this.kexKeyPair || !this.negotiated) {
			throw new Error('Received KEX_ECDH_REPLY in unexpected state');
		}

		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const serverHostKeyBlob = reader.readString();
		const serverPublicKey = reader.readString();
		const signatureBlob = reader.readString();

		// verify host key
		const accepted = await this.hostKeyVerifier(this.negotiated.hostKey, serverHostKeyBlob);
		if (!accepted) {
			await this.disconnect(
				SSH_DISCONNECT_HOST_KEY_NOT_VERIFIABLE,
				'Host key rejected by verifier',
			);
			return;
		}

		// compute shared secret
		const sharedSecretRaw = await computeSharedSecret(this.kexKeyPair.privateKey, serverPublicKey);
		const sharedSecret = rawBytesToMpint(sharedSecretRaw);

		// compute exchange hash H
		const exchangeHash = await computeExchangeHash({
			clientVersion: CLIENT_VERSION,
			serverVersion: this.serverVersion,
			clientKexInit: this.clientKexInitPayload!,
			serverKexInit: this.serverKexInitPayload!,
			serverHostKey: serverHostKeyBlob,
			clientPublicKey: this.kexKeyPair.publicKey,
			serverPublicKey,
			sharedSecret,
		});

		// verify the server's signature over the exchange hash
		const sigValid = await verifyHostKey(serverHostKeyBlob, signatureBlob, exchangeHash);
		if (!sigValid) {
			await this.disconnect(
				SSH_DISCONNECT_KEY_EXCHANGE_FAILED,
				'Host key signature verification failed',
			);
			return;
		}

		// first key exchange sets the session ID
		if (!this.sessionId) {
			this.sessionId = exchangeHash;
		}

		// derive session keys
		// aes128-gcm: 16-byte key, 12-byte IV (4 fixed + 8 counter), MAC unused (GCM AEAD)
		const keys = await deriveSessionKeys(
			sharedSecret,
			exchangeHash,
			this.sessionId,
			16, // enc key length (AES-128)
			12, // iv length (GCM)
			0, // mac key length (unused with GCM)
		);

		// create the cipher
		const newCipher = await Aes128GcmCipher.create(
			keys.encKeyClientToServer,
			keys.encKeyServerToClient,
			keys.ivClientToServer,
			keys.ivServerToClient,
		);

		// send NEWKEYS
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_NEWKEYS);
		await this.send(w.toUint8Array());

		this.logger.debug('sent NEWKEYS, cipher activated for outgoing packets');

		// activate cipher for outgoing packets immediately after sending NEWKEYS
		this.cipher = newCipher;
		this.sendSeq = 0;

		// store pending cipher for incoming activation on receiving NEWKEYS
		this.pendingRecvCipher = newCipher;

		this.state = TransportState.NewKeys;

		// clean up kex state
		this.kexKeyPair = null;
		this.clientKexInitPayload = null;
		this.serverKexInitPayload = null;
	}

	private pendingRecvCipher: PacketCipher | null = null;

	private async handleNewKeys(): Promise<void> {
		if (this.state !== TransportState.NewKeys || !this.pendingRecvCipher) {
			throw new Error('Received NEWKEYS in unexpected state');
		}

		// activate cipher for incoming packets
		this.packetReader.setCipher(this.pendingRecvCipher);
		this.recvSeq = 0;
		this.pendingRecvCipher = null;

		this.state = TransportState.Established;
		this.logger.debug('handshake complete, transport established');
		this.onReady.emit();
	}

	override dispose(): void {
		if (this.state !== TransportState.Disconnected) {
			this.state = TransportState.Disconnected;
		}
		// reject any pending ping waiters
		for (const waiter of this.pendingPongs) {
			waiter.reject(new Error('Transport disposed'));
		}
		this.pendingPongs.length = 0;
		this.onMessage.dispose();
		this.onDisconnect.dispose();
		this.onReady.dispose();
		this.onDebug.dispose();
		super.dispose();
	}
}

/**
 * Pick the first client-preferred algorithm that the server also supports.
 */
function negotiate(clientList: string[], serverList: string[], category: string): string {
	for (const alg of clientList) {
		if (serverList.includes(alg)) {
			return alg;
		}
	}
	throw new Error(
		`No matching ${category} algorithm. Client: [${clientList.join(', ')}], Server: [${serverList.join(', ')}]`,
	);
}
